import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountMembership, APIService, AuthorizationLevel, CreateUserInput, InviteUserInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { REQUIRED_TRIAL_REPRESENTATIVES } from '../../../../containers/default-layout/trial-reward';

export { REQUIRED_TRIAL_REPRESENTATIVES } from '../../../../containers/default-layout/trial-reward';

interface TeamMemberEntry {
  name: string;
  phone: any;
  email: string;
  status: 'pending' | 'inviting' | 'invited' | 'error';
  error?: string;
  userId?: string;
}

@Component({
  selector: 'ws-invite-team',
  templateUrl: './ws-invite-team.component.html',
  imports: [FormsModule, NgxIntlTelInputModule, MatButton]
})
export class WsInviteTeamComponent {
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<string[]>();
  @Output() stepReady = new EventEmitter<string[]>();

  members: TeamMemberEntry[] = [];
  currentName = '';
  currentPhone: any = null;
  currentEmail = '';
  isInviting = false;

  // Phone input config
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.India, CountryISO.UnitedStates];
  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  get invitedCount(): number {
    return this.members.filter(member => member.status === 'invited' && !!member.userId).length;
  }

  get requiredRepresentatives(): number {
    return REQUIRED_TRIAL_REPRESENTATIVES;
  }

  get pendingCount(): number {
    return this.members.filter(member => member.status === 'pending').length;
  }

  get teamMemberProgress(): number {
    return Math.min(this.invitedCount, REQUIRED_TRIAL_REPRESENTATIVES);
  }

  get invitationProgressPercent(): number {
    return Math.round((this.teamMemberProgress / REQUIRED_TRIAL_REPRESENTATIVES) * 100);
  }

  get remainingInvites(): number {
    return Math.max(0, REQUIRED_TRIAL_REPRESENTATIVES - this.invitedCount);
  }

  get hasFailedInvitations(): boolean {
    return this.members.some(member => member.status === 'error');
  }

  private isPhoneValid(): boolean {
    if (!this.currentPhone?.e164Number) return false;
    const parsed = parsePhoneNumberFromString(this.currentPhone.e164Number);
    return parsed?.isValid() === true;
  }

  addMember() {
    if (!this.currentPhone?.e164Number || !this.isPhoneValid()) {
      this.toastr.warning('Please enter a valid phone number');
      return;
    }
    // Check for duplicates
    const e164 = this.currentPhone.e164Number;
    if (this.members.some(m => m.phone?.e164Number === e164)) {
      this.toastr.warning('This number is already added');
      return;
    }
    this.members.push({
      name: this.currentName.trim() || '',
      phone: { ...this.currentPhone },
      email: this.currentEmail.trim() || '',
      status: 'pending',
    });
    this.currentName = '';
    this.currentPhone = null;
    this.currentEmail = '';
  }

  removeMember(member: TeamMemberEntry) {
    this.members = this.members.filter(m => m !== member);
  }

  async inviteAll() {
    const pending = this.members.filter(m => m.status === 'pending');
    if (pending.length === 0) {
      this.emitInvitationProgress();
      return;
    }
    this.isInviting = true;

    for (const member of pending) {
      member.status = 'inviting';
      try {
        if (this.testMode) {
          await new Promise(r => setTimeout(r, 300));
          member.status = 'invited';
          member.userId = 'test-user-' + Math.random().toString(36).slice(2, 8);
          continue;
        }

        const payload = <InviteUserInput>{};
        payload.invitedUser = <CreateUserInput>{};
        payload.invitedUser.registeredNumber = member.phone.e164Number;
        payload.invitedUser.name = member.name || '';
        payload.invitedUser.registeredEmail = member.email || undefined;
        payload.invitedUserAuthorizationLevel = AuthorizationLevel.MEMBER;
        payload['inviteeAccountId'] = this.activeAccount.accountId;

        const result = await this.api.InviteUser(payload);
        member.status = 'invited';
        member.userId = result?.id || '';
      } catch (e: any) {
        member.status = 'error';
        member.error = e?.errors?.[0]?.message || 'Failed to invite';
        console.error('Error inviting member:', e);
      }
    }

    const invited = this.members.filter(m => m.status === 'invited').length;
    if (invited > 0) {
      this.toastr.success(`${invited} team member(s) invited`);
    }

    this.isInviting = false;
    this.emitInvitationProgress();
  }

  private getInvitedUserIds(): string[] {
    return this.members
      .filter(m => m.status === 'invited' && m.userId)
      .map(m => m.userId);
  }

  private emitInvitationProgress(): void {
    const invitedUserIds = this.getInvitedUserIds();
    const failedCount = this.members.filter(member => member.status === 'error').length;
    this.completed.emit(invitedUserIds);
    if (invitedUserIds.length < REQUIRED_TRIAL_REPRESENTATIVES) {
      const remaining = REQUIRED_TRIAL_REPRESENTATIVES - invitedUserIds.length;
      this.toastr.info(`Invite ${remaining} more sales rep${remaining === 1 ? '' : 's'} toward your free admin seat, or skip for now.`);
    }
    if (failedCount > 0) {
      this.toastr.warning(`Fix or remove the failed rep${failedCount === 1 ? '' : 's'} and invite again.`);
      return;
    }
    if (invitedUserIds.length >= REQUIRED_TRIAL_REPRESENTATIVES) {
      this.stepReady.emit(invitedUserIds);
    }
  }
}
